import { useCallback, useEffect, useMemo, useState } from 'react'

import { getRuleSearchFilter, useRulesFilters } from './useRulesFilters'
import { useLastData } from '@/hooks/useLastData'
import { useDebounce } from 'use-debounce'

import { useRulesQuery } from '@/graphql/generated/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectIncidentRulesPageSize,
  setIncidentRulesNameLikeFilter,
  setIncidentRulesPageSize,
} from '@/redux/ui/uiSlice'

import type { RuleSortColumn, RuleSortOrder } from '../types/rulesTable'
import { getOrderBy } from '../utils/utils'

const SEARCH_DEBOUNCE_MS = 200
const DEFAULT_PAGE_SIZE = 25

export const useRulesPage = () => {
  const dispatch = useAppDispatch()
  const [nameFilter, setNameFilter] = useState('')
  const [debouncedNameFilter] = useDebounce(nameFilter, SEARCH_DEBOUNCE_MS)
  const { filters } = useRulesFilters()
  const pageSize =
    useAppSelector(selectIncidentRulesPageSize) ?? DEFAULT_PAGE_SIZE
  const [pageIndex, setPageIndex] = useState(0)
  const [sortColumn, setSortColumn] = useState<RuleSortColumn | null>(null)
  const [sortOrder, setSortOrder] = useState<RuleSortOrder | null>(null)

  const sortedColumn = useMemo(
    () =>
      sortColumn ? { id: sortColumn, isSortedDesc: sortOrder === 'desc' } : {},
    [sortColumn, sortOrder]
  )

  const {
    data,
    loading: isLoading,
    refetch,
  } = useRulesQuery({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: {
      filter: filters,
      first: pageIndex,
      last: pageSize,
      orderBy: getOrderBy(sortedColumn),
    },
  })

  const lastData = useLastData(data)
  const totalCount = lastData?.rules?.totalCount
  const rules = lastData?.rules?.edges ?? []

  useEffect(() => {
    const nameLikeFilter = getRuleSearchFilter(debouncedNameFilter)
    dispatch(setIncidentRulesNameLikeFilter(nameLikeFilter))
  }, [debouncedNameFilter, dispatch])

  useEffect(() => {
    setPageIndex(0)
  }, [filters])

  const handlePageChange = useCallback((nextPageIndex: number) => {
    setPageIndex(nextPageIndex)
  }, [])

  const handlePageSizeChange = useCallback(
    (nextPageSize: number) => {
      dispatch(setIncidentRulesPageSize(nextPageSize))
      setPageIndex(0)
    },
    [dispatch]
  )

  const handleSortChange = useCallback(
    (nextSortColumn: RuleSortColumn, nextSortOrder: RuleSortOrder) => {
      setSortColumn(nextSortColumn)
      setSortOrder(nextSortOrder)
      setPageIndex(0)
    },
    []
  )

  return {
    filters,
    isLoading,
    lastData,
    nameFilter,
    pageIndex,
    pageSize,
    refetch,
    rules,
    setNameFilter,
    sortColumn,
    sortOrder,
    totalCount,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
  }
}
